// src/feature-module/admin/CvBuilderAccessManagement.tsx
import React, { useEffect, useState } from "react";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaIdCard,
  FaCheck,
  FaTimes,
  FaUsers,
  FaEdit,
  FaLock,
  FaDownload,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type StudentAccess = {
  studentId: number;
  studentName: string;
  email: string;
  hasAccess: boolean;
  accessGrantedAt: string | null;
  grantedByName: string | null;
  downloadCount: number;
  lastDownloadAt: string | null;
};

type Stats = {
  totalStudentsWithAccess: number;
  totalAccessRecords: number;
  totalDownloads: number;
};

const CvBuilderAccessManagement: React.FC = () => {
  const [students, setStudents] = useState<StudentAccess[]>([]);
  const [filtered, setFiltered] = useState<StudentAccess[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAccess, setFilterAccess] = useState<"all" | "granted" | "denied">("all");
  const [filterDownloads, setFilterDownloads] = useState<"all" | "has_downloads" | "no_downloads">("all");
  const [stats, setStats] = useState<Stats | null>(null);
  const [toggling, setToggling] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selected, setSelected] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [profileModal, setProfileModal] = useState<{ studentId: number; studentName: string } | null>(null);
  const [firstNameInput, setFirstNameInput] = useState("");
  const [lastNameInput, setLastNameInput] = useState("");
  const [dobInput, setDobInput] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [accessRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/cv-builder/access`, { credentials: "include" }),
        fetch(`${API_BASE}/api/admin/cv-builder/stats`, { credentials: "include" }),
      ]);
      if (!accessRes.ok) throw new Error("Failed to load access data");
      const data: StudentAccess[] = await accessRes.json();
      setStudents(data);
      setFiltered(data);
      if (statsRes.ok) setStats(await statsRes.json());
      setLastUpdated(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const toggle = async (studentId: number) => {
    setToggling(studentId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/cv-builder/toggle/${studentId}`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      loadData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to toggle access");
    } finally {
      setToggling(null);
    }
  };

  const bulkGrant = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Grant CV Builder access to ${selected.length} student(s)?`)) return;
    try {
      await fetch(`${API_BASE}/api/admin/cv-builder/bulk-grant`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selected),
      });
      setSelected([]);
      loadData();
    } catch (e) {
      alert("Bulk grant failed");
    }
  };

  const bulkRevoke = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Revoke CV Builder access from ${selected.length} student(s)?`)) return;
    try {
      await fetch(`${API_BASE}/api/admin/cv-builder/bulk-revoke`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(selected),
      });
      setSelected([]);
      loadData();
    } catch (e) {
      alert("Bulk revoke failed");
    }
  };

  const openProfileModal = async (studentId: number, studentName: string) => {
    setProfileError("");
    const res = await fetch(`${API_BASE}/api/admin/cv-builder/profile/${studentId}`, { credentials: "include" });
    const profileData = res.ok ? await res.json().catch(() => null) : null;
    if (profileData) {
      setFirstNameInput(profileData.firstName ?? "");
      setLastNameInput(profileData.lastName ?? "");
      setDobInput(profileData.dateOfBirth ?? "");
    } else {
      // No CV profile yet — prefill from the student's registration name as a starting point
      const [first, ...rest] = studentName.trim().split(" ");
      setFirstNameInput(first ?? "");
      setLastNameInput(rest.join(" "));
      setDobInput("");
    }
    setProfileModal({ studentId, studentName });
  };

  const saveProfile = async () => {
    if (!profileModal) return;
    setProfileError("");
    if (!firstNameInput.trim()) {
      setProfileError("First name (Vorname) is required");
      return;
    }
    setProfileSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/cv-builder/profile/${profileModal.studentId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstNameInput.trim(),
          lastName: lastNameInput.trim(),
          dateOfBirth: dobInput,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setProfileError(err.error ?? "Failed to update");
        return;
      }
      setProfileModal(null);
      loadData();
    } catch {
      setProfileError("Network error");
    } finally {
      setProfileSaving(false);
    }
  };

  const toggleSelect = (id: number) =>
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);

  const selectAll = () =>
    setSelected(
      selected.length === paginated.length ? [] : paginated.map(s => s.studentId)
    );

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    let f = students;
    if (filterAccess === "granted") f = f.filter(s => s.hasAccess);
    else if (filterAccess === "denied") f = f.filter(s => !s.hasAccess);
    if (filterDownloads === "has_downloads") f = f.filter(s => (s.downloadCount ?? 0) > 0);
    else if (filterDownloads === "no_downloads") f = f.filter(s => (s.downloadCount ?? 0) === 0);
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      f = f.filter(s => s.studentName.toLowerCase().includes(t) || s.email.toLowerCase().includes(t));
    }
    setFiltered(f);
    setCurrentPage(1);
    setSelected([]);
  }, [students, searchTerm, filterAccess, filterDownloads]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaIdCard />}
        title="CV Builder Access Management"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "CV Builder Access" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-lg">
            {error}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaCheck className="text-blue-600 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Students with Access</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalStudentsWithAccess}</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FaUsers className="text-purple-600 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Students</p>
                <p className="text-2xl font-bold text-gray-900">{students.length}</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaDownload className="text-green-600 text-xl" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Total CV Downloads</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalDownloads}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters + bulk actions */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Search</label>
              <input
                type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Name or email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Access</label>
              <select value={filterAccess} onChange={e => setFilterAccess(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3]">
                <option value="all">All Students</option>
                <option value="granted">Access Granted</option>
                <option value="denied">No Access</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Downloads</label>
              <select value={filterDownloads} onChange={e => setFilterDownloads(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3]">
                <option value="all">All</option>
                <option value="has_downloads">Has Downloads</option>
                <option value="no_downloads">Never Downloaded</option>
              </select>
            </div>
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
              <span className="text-sm font-semibold text-gray-600">{selected.length} selected</span>
              <button onClick={bulkGrant}
                className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
                Grant All
              </button>
              <button onClick={bulkRevoke}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold">
                Revoke All
              </button>
              <button onClick={() => setSelected([])} className="text-sm text-gray-500 hover:text-gray-700">
                Clear selection
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-600">
              {filtered.length} student{filtered.length !== 1 ? "s" : ""}
            </span>
            <span className="text-xs text-gray-400">
              Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input type="checkbox" checked={selected.length === paginated.length && paginated.length > 0}
                      onChange={selectAll} className="rounded" />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Student</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">CV Builder Access</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">CV Downloads</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Last Download</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Granted At</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Granted By</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Profile</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>
                )}
                {!loading && paginated.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">No students found</td></tr>
                )}
                {!loading && paginated.map(s => (
                  <tr key={s.studentId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.includes(s.studentId)}
                        onChange={() => toggleSelect(s.studentId)} className="rounded" />
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-gray-900">{s.studentName}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{s.email}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggle(s.studentId)}
                        disabled={toggling === s.studentId}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-60 ${
                          s.hasAccess
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-red-100 text-red-800 hover:bg-red-200"
                        }`}
                      >
                        {toggling === s.studentId
                          ? "..."
                          : s.hasAccess
                            ? <><FaCheck size={10} /> Granted</>
                            : <><FaTimes size={10} /> Denied</>
                        }
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(s.downloadCount ?? 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                          <FaDownload size={9} /> {s.downloadCount}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {s.lastDownloadAt
                        ? new Date(s.lastDownloadAt).toLocaleDateString("de-DE")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {s.accessGrantedAt
                        ? new Date(s.accessGrantedAt).toLocaleDateString("de-DE")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{s.grantedByName ?? "—"}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openProfileModal(s.studentId, s.studentName)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                        title="Edit name and date of birth"
                      >
                        <FaEdit size={10} /> Edit Profile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filtered.length > 0 && (
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Per page:</label>
                  <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-[#009fe3]">
                    {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-white">
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const p = totalPages <= 5 ? i + 1
                      : currentPage <= 3 ? i + 1
                      : currentPage >= totalPages - 2 ? totalPages - 4 + i
                      : currentPage - 2 + i;
                    return (
                      <button key={p} onClick={() => setCurrentPage(p)}
                        className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                          currentPage === p ? "bg-[#009fe3] text-white" : "border border-gray-300 hover:bg-white"
                        }`}>
                        {p}
                      </button>
                    );
                  })}
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-white">
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Edit Profile Modal */}
      {profileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <FaLock className="text-[#009fe3]" />
              <h3 className="text-lg font-bold text-gray-900">Edit CV Profile</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Student: <span className="font-semibold text-gray-800">{profileModal.studentName}</span>
            </p>

            <label className="block text-sm font-semibold text-gray-700 mb-1">
              First Name (Vorname)
            </label>
            <input
              type="text"
              value={firstNameInput}
              onChange={e => setFirstNameInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent mb-3"
            />

            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Last Name (Nachname)
            </label>
            <input
              type="text"
              value={lastNameInput}
              onChange={e => setLastNameInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent mb-3"
            />

            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={dobInput}
              onChange={e => setDobInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent mb-3"
            />

            {profileError && <p className="text-xs text-red-600 mb-3">{profileError}</p>}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setProfileModal(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveProfile}
                disabled={profileSaving || !firstNameInput.trim()}
                className="px-4 py-2 text-sm bg-[#009fe3] text-white rounded-lg hover:bg-[#0088cc] disabled:opacity-50 font-semibold"
              >
                {profileSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CvBuilderAccessManagement;
